/**
 * Copyright © 2026 Delicious AI, LLC
 */

import { TrendingDown, TrendingUp } from '@mui/icons-material'
import { Box, Tooltip, useTheme } from '@mui/material'
import React from 'react'

import useT from '@/i18n/useT'

interface TrendIndicatorProps {
  value: number
  suffix?: string
  prefix?: string
}

const TrendIndicator: React.FC<TrendIndicatorProps> = ({
  value,
  suffix,
  prefix,
}) => {
  const theme = useTheme()
  const { t } = useT()
  const isFiniteValue = Number.isFinite(value)
  const trendDirection: 'up' | 'down' | 'flat' | null = !isFiniteValue
    ? null
    : value > 0
      ? 'up'
      : value < 0
        ? 'down'
        : 'flat'
  const formattedValue =
    isFiniteValue && trendDirection !== null
      ? `${trendDirection === 'up' ? '+' : trendDirection === 'down' ? '-' : ''}${prefix || ''}${Math.abs(value).toFixed(2)}`
      : '—'
  const color =
    !isFiniteValue || trendDirection === 'flat'
      ? theme.palette.text.secondary
      : trendDirection === 'up'
        ? theme.palette.success.main
        : theme.palette.error.main

  return (
    <Tooltip title={t('common.trendIndicator.yearOverYear')} arrow>
      <Box
        component="span"
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.25,
          color,
          fontSize: '0.875rem',
        }}
      >
        {trendDirection === null ||
        trendDirection === 'flat' ? null : trendDirection === 'up' ? (
          <TrendingUp sx={{ fontSize: '1rem' }} />
        ) : (
          <TrendingDown sx={{ fontSize: '1rem' }} />
        )}
        {formattedValue}
        {suffix && <span>{suffix}</span>}
      </Box>
    </Tooltip>
  )
}

export default TrendIndicator
